// Pan and zoom for the mind map's canvas. The stage is drawn in its own
// coordinates and shown through `translate(x, y) scale(k)`.
//
// Drag the background (mouse, pen or finger) or scroll to pan; Ctrl/⌘ + wheel
// or a trackpad pinch zooms around the pointer.
import { useCallback, useEffect, useRef, useState } from 'react';

export interface View {
  x: number;
  y: number;
  k: number;
}

export const MIN_ZOOM = 0.25;
export const MAX_ZOOM = 2;
const clampZoom = (k: number) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, k));

export function usePanZoom(viewportRef: React.RefObject<HTMLDivElement | null>) {
  const [view, setView] = useState<View>({ x: 0, y: 0, k: 1 });
  const viewRef = useRef(view);
  viewRef.current = view;

  const zoomAt = useCallback((k: number, at?: { x: number; y: number }) => {
    const el = viewportRef.current;
    const v = viewRef.current;
    const next = clampZoom(k);
    const px = at?.x ?? (el ? el.clientWidth / 2 : 0);
    const py = at?.y ?? (el ? el.clientHeight / 2 : 0);
    // Keep the stage point under (px, py) where it is.
    const sx = (px - v.x) / v.k;
    const sy = (py - v.y) / v.k;
    setView({ k: next, x: px - sx * next, y: py - sy * next });
  }, [viewportRef]);

  /** Puts a stage point in the middle of the viewport. */
  const centerOn = useCallback((point: { x: number; y: number }, k = viewRef.current.k) => {
    const el = viewportRef.current;
    if (!el) return;
    setView({ k, x: el.clientWidth / 2 - point.x * k, y: el.clientHeight / 2 - point.y * k });
  }, [viewportRef]);

  /**
   * Zooms out (never in past 100%) until the whole map fits — centred, or
   * against the left edge for a map that grows to the right. A map too big to
   * fit even fully zoomed out is framed on `home` (the central topic) instead:
   * the middle of its bounding box can be empty space between branches, which
   * left the view on a blank patch.
   */
  const fit = useCallback((
    b: { minX: number; minY: number; maxX: number; maxY: number },
    anchor: 'center' | 'left' = 'center',
    home?: { x: number; y: number; w: number; h: number }
  ) => {
    const el = viewportRef.current;
    if (!el || !el.clientWidth || !el.clientHeight || !Number.isFinite(b.minX)) return;
    const pad = 48;
    const w = Math.max(1, b.maxX - b.minX);
    const h = Math.max(1, b.maxY - b.minY);
    const wanted = Math.min(1, (el.clientWidth - pad * 2) / w, (el.clientHeight - pad * 2) / h);
    const k = clampZoom(wanted);
    const fits = wanted >= MIN_ZOOM;
    if (!fits && home) {
      const hy = home.y + home.h / 2;
      if (anchor === 'left') setView({ k, x: pad - home.x * k, y: el.clientHeight / 2 - hy * k });
      else centerOn({ x: home.x + home.w / 2, y: hy }, k);
      return;
    }
    if (anchor === 'left') {
      setView({ k, x: pad - b.minX * k, y: el.clientHeight / 2 - ((b.minY + b.maxY) / 2) * k });
      return;
    }
    centerOn({ x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 }, k);
  }, [viewportRef, centerOn]);

  /**
   * Back to 100%, framed on a stage rectangle (the central topic): centred, or
   * against the left edge for a map that grows to the right.
   */
  const home = useCallback((r: { x: number; y: number; w: number; h: number }, anchor: 'center' | 'left' = 'center') => {
    const el = viewportRef.current;
    if (!el) return;
    const pad = 48;
    const cy = r.y + r.h / 2;
    if (anchor === 'left') setView({ k: 1, x: pad - r.x, y: el.clientHeight / 2 - cy });
    else centerOn({ x: r.x + r.w / 2, y: cy }, 1);
  }, [viewportRef, centerOn]);

  /** Pans just enough to bring a stage rectangle into view. */
  const reveal = useCallback((r: { x: number; y: number; w: number; h: number }) => {
    const el = viewportRef.current;
    if (!el) return;
    const v = viewRef.current;
    const pad = 40;
    const left = v.x + r.x * v.k;
    const top = v.y + r.y * v.k;
    const right = left + r.w * v.k;
    const bottom = top + r.h * v.k;
    let dx = 0;
    let dy = 0;
    if (left < pad) dx = pad - left;
    else if (right > el.clientWidth - pad) dx = el.clientWidth - pad - right;
    if (top < pad) dy = pad - top;
    else if (bottom > el.clientHeight - pad) dy = el.clientHeight - pad - bottom;
    if (dx || dy) setView({ ...v, x: v.x + dx, y: v.y + dy });
  }, [viewportRef]);

  // Wheel: needs a non-passive listener to stop the page scrolling.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const v = viewRef.current;
      if (e.ctrlKey || e.metaKey) {
        const rect = el.getBoundingClientRect();
        zoomAt(v.k * Math.exp(-e.deltaY * 0.0025), { x: e.clientX - rect.left, y: e.clientY - rect.top });
      } else {
        setView({ ...v, x: v.x - e.deltaX, y: v.y - e.deltaY });
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [viewportRef, zoomAt]);

  // Background drag to pan.
  const panRef = useRef<{ id: number; x: number; y: number; vx: number; vy: number } | null>(null);
  const backgroundHandlers = {
    onPointerDown: (e: React.PointerEvent) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      panRef.current = { id: e.pointerId, x: e.clientX, y: e.clientY, vx: viewRef.current.x, vy: viewRef.current.y };
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    },
    onPointerMove: (e: React.PointerEvent) => {
      const p = panRef.current;
      if (!p || p.id !== e.pointerId) return;
      setView({ ...viewRef.current, x: p.vx + e.clientX - p.x, y: p.vy + e.clientY - p.y });
    },
    onPointerUp: (e: React.PointerEvent) => {
      if (panRef.current?.id === e.pointerId) panRef.current = null;
    },
    onPointerCancel: () => {
      panRef.current = null;
    },
  };

  return { view, zoomAt, centerOn, fit, home, reveal, backgroundHandlers, isPanning: () => !!panRef.current };
}
